import { useState } from "react";
import { Circle, Marker, Popup } from "react-leaflet";
import { Map } from "@/components/ui/map";
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import iconRetina from "leaflet/dist/images/marker-icon-2x.png";
import { Slider } from "@/components/ui/slider";
import "leaflet/dist/leaflet.css";
import L, { LatLngTuple } from "leaflet";
import { useWorkOrderRecord } from "@/app/ProDrivers/WorkOrders/Hooks/useWorkOrderRecord";
import { useTranslations } from "use-intl";
import { ProDriverTable } from "@/app/ProDrivers/Drivers/Components/ProDriverTable";
import { WarningBanner } from "@/lib/Components/Banners/WarningBanner";
import { WorkOrderJobOpenings } from "@/app/ProDrivers/WorkOrders/Components/WorkOrderJobOpenings";
import { BasemapLayer } from "@/components/map/BasemapLayer";

const DEFAULT_CENTER: LatLngTuple = [-25.2744, 133.7751];

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconRetinaUrl: iconRetina,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

L.Marker.prototype.options.icon = DefaultIcon;

export function WorkOrderDriversScreen() {
  return (
    <div className="w-full space-y-10 py-10">
      <WorkOrderJobOpenings />
      <WorkOrderDriverSearch />
    </div>
  );
}

function WorkOrderDriverSearch() {
  const t = useTranslations("workOrder.driversScreen");
  const tDetails = useTranslations("workOrder.details");
  const { departureAddress, deliveryAddress } = useWorkOrderRecord();

  const departureHasCoords =
    departureAddress.lat != null && departureAddress.lng != null;
  const deliveryHasCoords =
    deliveryAddress.lat != null && deliveryAddress.lng != null;

  const initialMode: "departure" | "delivery" = departureHasCoords
    ? "departure"
    : deliveryHasCoords
      ? "delivery"
      : "departure";

  const [locationMode, setLocationMode] = useState<"departure" | "delivery">(
    initialMode,
  );
  const [radius, setRadius] = useState(100);

  const address =
    locationMode === "departure" ? departureAddress : deliveryAddress;
  const hasCoords =
    locationMode === "departure" ? departureHasCoords : deliveryHasCoords;
  const center: LatLngTuple = hasCoords
    ? [address.lat!, address.lng!]
    : DEFAULT_CENTER;

  const handleRadiusChange = (value: [number, number]) => {
    setRadius(value[0]);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">{t("title")}</h3>

      {!hasCoords ? (
        <WarningBanner>
          {locationMode === "departure"
            ? t("missingDepartureCoords")
            : t("missingDeliveryCoords")}
        </WarningBanner>
      ) : null}
      <div className="relative overflow-hidden rounded-lg border shadow-lg">
        <Map
          key={locationMode}
          center={center}
          zoom={5}
          scrollWheelZoom={false}
          height="300px"
        >
          <BasemapLayer />

          <Marker position={center}>
            <Popup>
              <div className="text-center">
                <strong>{t("searchCenter")}</strong>
                <br />
                {t("lookingForDrivers", { radius })}
              </div>
            </Popup>
          </Marker>

          <Circle
            center={center}
            radius={radius * 1000}
            pathOptions={{
              fillColor: "#3b82f6",
              fillOpacity: 0.1,
              color: "#3b82f6",
              weight: 2,
              opacity: 0.6,
            }}
          />
        </Map>

        <div className="absolute right-4 top-4 z-[1000] w-64 rounded-lg border bg-white/95 p-4 shadow-lg backdrop-blur-sm">
          <div className="space-y-3">
            <div className="flex rounded-md bg-gray-100 p-0.5">
              <button
                type="button"
                onClick={() => setLocationMode("departure")}
                className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                  locationMode === "departure"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tDetails("pickUpLocation")}
              </button>
              <button
                type="button"
                onClick={() => setLocationMode("delivery")}
                className={`flex-1 whitespace-nowrap rounded px-2 py-1 text-xs font-medium transition-colors ${
                  locationMode === "delivery"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tDetails("dropOffLocation")}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">
                {t("searchRadius")}
              </label>
              <span className="text-sm text-gray-500">{radius} km</span>
            </div>
            <Slider
              value={[radius]}
              onValueChange={handleRadiusChange}
              max={500}
              min={50}
              step={25}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-gray-400">
              <span>50km</span>
              <span>500km</span>
            </div>
          </div>
        </div>
      </div>

      <ProDriverTable
        id={"paid-driver-list-screen-old"}
        queryVariables={
          hasCoords
            ? {
                radiusAround: { radius, lat: center[0], lng: center[1] },
              }
            : {}
        }
      />
    </div>
  );
}
